import { eventBus, EVENTS } from '../../../core/events/index.js';

export class RankingsState {
  constructor() {
    this.characters = [];
    this.currentPage = 1;
    this.totalPages = 1;
    this.totalCharacters = 0;
    this.itemsPerPage = 15;
    this.hasMoreCharacters = false;
    this.isLoading = false;
    this.error = null;
    this.filters = {
      nameFilter: ''
    };
    this.selectedCharacterId = null;
    this.scenarios = [];
    this.favoriteCharacterIds = [];
    this.privateCharacterIds = [];
  }

  _validateCharacter(character) {
    if (!character?.id) return null;

    return {
      ...character,
      name: character.name || 'Unknown Character',
      rank: character.rank || '?',
      image: character.image || '/assets/images/kiss.webp',
      imageBack: character.imageBack || character.image || '/assets/images/kiss.webp',
      creator: character.creator || 'Unknown'
    };
  }

  _emitUpdate(event, data) {
    eventBus.emit(event, data);
  }

  _getMetadata() {
    return {
      currentPage: this.currentPage,
      totalPages: this.totalPages,
      totalCharacters: this.totalCharacters,
      hasMoreCharacters: this.hasMoreCharacters
    };
  }

  _syncHasMore() {
    this.hasMoreCharacters = this.characters.length < (this.totalCharacters || 0);
  }

  _syncPagingFromCounts() {
    const perPage = this.itemsPerPage || 15;
    this.totalPages = Math.max(1, Math.ceil((this.totalCharacters || 0) / perPage));
    this._syncHasMore();
  }

  setCharacters(characters, metadata = {}) {
    if (!Array.isArray(characters)) {
      console.error('Invalid characters array:', characters);
      characters = [];
    }

    this.characters = characters.map(c => this._validateCharacter(c)).filter(Boolean);
    this.totalPages = metadata.totalPages || 1;
    this.totalCharacters = metadata.totalCharacters || this.characters.length;
    this._syncHasMore();

    this._emitUpdate(EVENTS.RANKINGS.CHARACTERS_UPDATED, {
      characters: this.characters,
      metadata: this._getMetadata()
    });
  }

  updateCharacters(updatedCharacters) {
    if (!Array.isArray(updatedCharacters) || !Array.isArray(this.characters)) return;

    updatedCharacters.forEach(updated => {
      const index = this.characters.findIndex(c => String(c.id) === String(updated.id));
      if (index !== -1) {
        Object.assign(this.characters[index], updated);
      }
    });

    this._emitUpdate(EVENTS.RANKINGS.CHARACTERS_UPDATED, {
      characters: this.characters,
      metadata: this._getMetadata()
    });
  }

  addCharacters(characters, metadata = {}) {
    if (!Array.isArray(characters)) {
      console.error('Invalid characters array:', characters);
      characters = [];
    }

    const newCharacters = characters.map(c => this._validateCharacter(c)).filter(Boolean);
    this.characters = [...this.characters, ...newCharacters];
    this.totalPages = metadata.totalPages || this.totalPages;
    this.totalCharacters = metadata.totalCharacters || this.characters.length;
    this._syncHasMore();

    this._emitUpdate(EVENTS.RANKINGS.CHARACTERS_UPDATED, {
      characters: this.characters,
      metadata: this._getMetadata()
    });
  }

  removeCharactersByIds(ids) {
    const idSet = new Set((ids || []).map((id) => String(id)).filter(Boolean));
    if (!idSet.size || !Array.isArray(this.characters)) return 0;

    const next = this.characters.filter((character) => !idSet.has(String(character.id)));
    const removed = this.characters.length - next.length;
    if (!removed) return 0;

    this.characters = next;
    this.totalCharacters = Math.max(0, (this.totalCharacters || 0) - removed);
    this._syncPagingFromCounts();
    return removed;
  }

  setCurrentPage(page) {
    this.currentPage = page;
    this._syncHasMore();
    this._emitUpdate(EVENTS.RANKINGS.PAGE_CHANGED, {
      currentPage: this.currentPage,
      hasMoreCharacters: this.hasMoreCharacters
    });
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;
    this._emitUpdate(EVENTS.RANKINGS.LOADING_CHANGED, { isLoading });
  }

  setError(error) {
    this.error = error;
    this._emitUpdate(EVENTS.RANKINGS.ERROR_CHANGED, { error });
  }

  setFilters(filters) {
    this.filters = { ...this.filters, ...filters };
    this._emitUpdate(EVENTS.RANKINGS.FILTERS_CHANGED, { filters: this.filters });
  }

  setSelectedCharacterId(characterId) {
    this.selectedCharacterId = characterId;
  }

  setScenarios(scenarios) {
    this.scenarios = Array.isArray(scenarios) ? scenarios : [];
    this._emitUpdate(EVENTS.RANKINGS.SCENARIOS_UPDATED, { scenarios: this.scenarios });
  }

  setFavoriteCharacterIds(favoriteIds) {
    this.favoriteCharacterIds = Array.isArray(favoriteIds) ? favoriteIds : [];
    this._emitUpdate(EVENTS.RANKINGS.FAVORITES_UPDATED, { favoriteIds: this.favoriteCharacterIds });
  }

  isCharacterFavorited(characterId) {
    return this.favoriteCharacterIds.includes(parseInt(characterId));
  }

  toggleCharacterFavorite(characterId) {
    const id = parseInt(characterId);
    const index = this.favoriteCharacterIds.indexOf(id);

    if (index >= 0) {
      this.favoriteCharacterIds.splice(index, 1);
    } else {
      this.favoriteCharacterIds.push(id);
    }

    this._emitUpdate(EVENTS.RANKINGS.FAVORITES_UPDATED, { favoriteIds: this.favoriteCharacterIds });
    return this.favoriteCharacterIds.includes(id);
  }

  setPrivateCharacterIds(privateIds) {
    this.privateCharacterIds = Array.isArray(privateIds) ? privateIds : [];
    this._emitUpdate(EVENTS.RANKINGS.PRIVATE_UPDATED, { privateIds: this.privateCharacterIds });
  }
}
